import { captureException } from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuestionnaireAnswersState } from "../components/contact-form/utils/questionnaireStorage";
import { VerificationCode } from "../components/contact-form/VerificationCode";
import { VerificationCodeVariantA } from "../components/contact-form/VerificationCodeVariantA";
import { LandingPageLayout } from "../components/LandingPageLayout";
import { useAnalytics } from "../hooks/useAnalytics";
import useLandingPageIds from "../hooks/useLandingPageIds";
import { useQuestionnaireQueryStates } from "../hooks/useQueryParams";
import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { LandingPageConfig } from "../types/landingPage";
import { getApiUrl } from "../utils/apiConfig";
import { identify } from "../utils/rudderstack";
import { prepareExistingPatientSubmissionPayload } from "../components/contact-form/api/preparePayload";
import { sentryFetch } from "../utils/sentryFetchWrapper";
import { useNavigateToRedirectPage } from "../hooks/useNavigateToRedirectPage.ts";
import { usePartnerConfig, mergePartnerConfig } from "../hooks/usePartnerConfig";
import { GoogleLoginButton } from "../components/GoogleLoginButton";
import { useFeatureFlag } from "../hooks/useFeatureFlags.ts";

/**
 * Content component for verification page
 * Designed to be rendered within the LandingPageFactory system
 */
export function VerificationPageContent({
  config,
}: {
  config: LandingPageConfig;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const partnerConfig = usePartnerConfig();

  const [error, setError] = useState<string | undefined>();
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasTrackedVerificationView, setHasTrackedVerificationView] =
    useState(false);

  const [questionnaireQueryState, setQuestionnaireQueryState] =
    useQuestionnaireQueryStates();
  const { formData, apiResponse } = questionnaireQueryState;
  const email = apiResponse?.outcome?.emailHint;
  const { variantId, masterId } = useLandingPageIds();
  const [questionnaireAnswers] = useQuestionnaireAnswersState();

  const navigateToRedirectPage = useNavigateToRedirectPage();

  const hasQuestionnaire =
    !!questionnaireAnswers.questionAnswers &&
    Object.keys(questionnaireAnswers.questionAnswers).length > 0;

  const { track } = useAnalytics();

  const allowGoogleLoginForExistingPatients = useFeatureFlag('allowGoogleLoginForExistingPatients', false) || import.meta.env.DEV;
  const useVerificationCodeVariantA = useFeatureFlag('useVerificationCodeVariantA', false);

  // Redirect if no data is found
  useEffect(() => {
    if (!email) {
      navigate(`/${config.id}`);
    }
  }, [email, navigate, config.id]);

  // Track when verification screen is viewed
  useEffect(() => {
    if (!hasTrackedVerificationView) {
      // Track verification screen view
      track("lp.verification.screen_viewed", {
        landing_page_id: config.id,
        variant_id: variantId,
        master_id: masterId,
        verification_type: verificationType,
        has_questionnaire_data: hasQuestionnaire,
        login_hint_provided: loginHint ? true : false,
        direct_load: true,
        allowGoogleLoginForExistingPatients,
        useVerificationCodeVariantA,
      });

      // Mark as tracked to avoid duplicate events
      setHasTrackedVerificationView(true);
    }
  }, [hasTrackedVerificationView, config.id, email]);

  // Scroll to button when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end' 
        });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  // Handle verification code submission
  const handleVerificationSubmit = async (code: string) => {
    try {
      setIsVerifying(true);
      setError(undefined);

      if (!email) {
        throw new Error("No email found for verification");
      }

      const payload = prepareExistingPatientSubmissionPayload({
        formData,
        questionnaireAnswers,
        landingPageId: config.id,
        userCredentials: {
          email,
          loginCode: code,
        },
      });

      const response = await sentryFetch(getApiUrl("refer-existing-patient"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
        ignoredStatusCodes: [401],
      });

      const data = await response.json();

      if (response.status === 401) {
        setError("Invalid verification code. Please try again.");

        track("lp.verification.code_invalid", {
          landing_page_id: config.id,
          has_questionnaire_data: hasQuestionnaire,
          allowGoogleLoginForExistingPatients,
          useVerificationCodeVariantA,
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`API error (${response.status})`);
      }

      track("lp.verification.completed", {
        landing_page_id: config.id,
        has_questionnaire_data: hasQuestionnaire,
        allowGoogleLoginForExistingPatients,
        useVerificationCodeVariantA,
        isNewPatient: false,
      });

      if (data?.outcome?.patientId) {
        identify(data.outcome.patientId);
      }

      const newQuestionnaireQueryState = {
        ...questionnaireQueryState,
        apiResponse: data,
      };

      setQuestionnaireQueryState(newQuestionnaireQueryState);
      navigateToRedirectPage(
        newQuestionnaireQueryState,
        questionnaireAnswers,
        config.id,
      );
    } catch (error) {
      captureException(error);

      const errorDetails = {
        message: error instanceof Error ? error.message : String(error),
      };

      if (error instanceof Error) {
        Object.assign(errorDetails, {
          ...((error as any).response && { response: (error as any).response }),
          ...((error as any).status && { status: (error as any).status }),
          ...((error as any).data && { data: (error as any).data }),
        });
      }

      track("lp.verification.error", {
        landing_page_id: config.id,
        error_message: error instanceof Error ? error.message : "Unknown error",
        allowGoogleLoginForExistingPatients,
        useVerificationCodeVariantA,
      });

      setError(
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle Google login
  const handleGoogleLogin = async (credential: string) => {
    try {
      setIsVerifying(true);
      setError(undefined);

      if (!email) {
        throw new Error("No email found for verification");
      }

      const payload = prepareExistingPatientSubmissionPayload({
        formData,
        questionnaireAnswers,
        landingPageId: config.id,
        userCredentials: {
          email,
          googleJwtToken: credential,
        },
      });

      const response = await sentryFetch(getApiUrl("refer-existing-patient"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
        ignoredStatusCodes: [401],
      });

      const data = await response.json();

      if (response.status === 401) {
        setError("Google login failed. Please try again or use verification code.");
        
        track("lp.verification.google_invalid", {
          landing_page_id: config.id,
          has_questionnaire_data: hasQuestionnaire,
          allowGoogleLoginForExistingPatients,
          useVerificationCodeVariantA,
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`API error (${response.status})`);
      }

      track("lp.verification.google_completed", {
        landing_page_id: config.id,
        has_questionnaire_data: hasQuestionnaire,
        allowGoogleLoginForExistingPatients,
        useVerificationCodeVariantA,
        isNewPatient: false,
      });

      if (data?.outcome?.patientId) {
        identify(data.outcome.patientId);
      }

      const newQuestionnaireQueryState = {
        ...questionnaireQueryState,
        apiResponse: data,
      };

      setQuestionnaireQueryState(newQuestionnaireQueryState);
      navigateToRedirectPage(
        newQuestionnaireQueryState,
        questionnaireAnswers,
        config.id,
      );
    } catch (error) {
      captureException(error);

      track("lp.verification.google_error", {
        landing_page_id: config.id,
        error_message: error instanceof Error ? error.message : "Unknown error",
        allowGoogleLoginForExistingPatients,
        useVerificationCodeVariantA,
      });

      setError(
        error instanceof Error
          ? error.message
          : "Google login failed. Please try again.",
      );
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle requesting a new verification code
  const handleResendCode = async (preferredLoginType?: "email" | "sms") => {
    try {
      setIsResendingCode(true);
      setError(undefined);

      if (!email) {
        throw new Error("No email found for verification");
      }

      const graphqlQuery = `
        mutation {
          sendLoginCodeNew(input: "${email}", preferredLoginCodeFlowType: ${preferredLoginType?.toUpperCase() || "SMS"}) {
            loginCodeDestination
            loginCodeFlowType
          }
        }
      `;
      const payload = {
        query: graphqlQuery,
      };

      const response = await sentryFetch(getApiUrl("/graphql"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        ignoredStatusCodes: [401],
      });

      if (!response.ok) {
        const errorData = await response.json().catch((e) => {
          captureException(e);
          return {
            message: `Failed to resend code: ${response.status}`,
          };
        });
        throw new Error(
          errorData?.message || `Failed to resend code: ${response.status}`,
        );
      }

      track("lp.verification.code_resent", {
        landing_page_id: config.id,
        has_questionnaire_data: hasQuestionnaire,
        preferred_login_type: preferredLoginType || "sms",
        allowGoogleLoginForExistingPatients,
        useVerificationCodeVariantA,
      });
    } catch (error) {
      captureException(error);

      track("lp.verification.resend_error", {
        landing_page_id: config.id,
        error_message: error instanceof Error ? error.message : "Unknown error",
        preferred_login_type: preferredLoginType || "sms",
        allowGoogleLoginForExistingPatients,
        useVerificationCodeVariantA,
      });

      setError(
        error instanceof Error
          ? error.message
          : "Failed to resend code. Please try again.",
      );
    } finally {
      setIsResendingCode(false);
    }
  };

  // If no data, show nothing (will redirect in useEffect)
  if (!email) {
    return null;
  }

  // Get login hint and verification type from API response or defaults
  const loginHint = apiResponse?.outcome?.loginHint || email;
  const verificationType =
    apiResponse?.outcome?.loginType === "SEND_LOGIN_CODE_SMS" ? "sms" : "email";
  const smsLoginSupported = apiResponse?.outcome?.smsLoginSupported || false;

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white" data-testid="verification-page">
        {/* Background orbs container - only visible on larger screens - matches ExtendedLandingPage */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          {/* Large icon */}
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>

          {/* Medium icon */}
          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>

          {/* Small pulsing icon */}
          <div className="absolute bottom-48 right-8 w-[120px] h-[120px] md:right-32 md:w-[180px] md:h-[180px] text-primary-500 floating-orb-pulse">
            <HealthMatchIcon />
          </div>
        </div>

        {/* Main content container - reduced top padding on mobile - matches ExtendedLandingPage */}
        <div
          className="relative z-10 isolation max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-16 pb-12 md:pb-24"
          data-testid="verification-content"
        >
          <div className="text-center mb-4 md:mb-12">
            {/* Title and subtitle */}
            <h1
              className="text-[1.625rem] leading-[1.3] md:leading-[1.2] tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl px-2 sm:px-4"
              data-testid="verification-title"
            >
              <span className="block mb-3 md:mb-4">{config.heroTitle}</span>
              {config.heroSubtitle && (
                <span className="block text-[1.375rem] md:text-[2rem] lg:text-[2.75rem] bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600 leading-[1.4] md:leading-[1.3] overflow-visible">
                  {config.heroSubtitle}
                </span>
              )}
            </h1>
            {/* Intro paragraph if provided - more compact on mobile */}
            {config.introText && !useVerificationCodeVariantA && (
              <div
                className="mt-6 md:mt-8 max-w-md mx-auto"
                data-testid="verification-intro"
              >
                <div className="inline-flex items-center justify-center text-secondary-600 bg-secondary-50/80 px-4 py-2 rounded-full border border-secondary-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="h-4 w-4 text-secondary-500 mr-2 flex-shrink-0"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm">{config.introText}</span>
                </div>
              </div>
            )}

            {config.longDescription && !useVerificationCodeVariantA && (
              <div
                className="mt-6 md:mt-8 max-w-3xl mx-auto px-2"
                data-testid="verification-description"
              >
                {config.longDescription.split("\n").map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-left text-sm md:text-base text-gray-700 leading-relaxed mb-3 last:mb-0"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div ref={containerRef} className="grid grid-cols-1 gap-4 md:gap-8">
            <div
              className="w-full max-w-4xl mx-auto px-2"
              data-testid="verification-form-container"
            >
              {useVerificationCodeVariantA && 
              <VerificationCodeVariantA
                onSubmit={handleVerificationSubmit}
                onResendCode={handleResendCode}
                loginHint={loginHint}
                emailHint={email}
                error={error}
                isLoading={isResendingCode || isVerifying}
                verificationType={verificationType}
                smsLoginSupported={smsLoginSupported}
              />} 
              {!useVerificationCodeVariantA && <VerificationCode
              onSubmit={handleVerificationSubmit}
              onResendCode={handleResendCode}
              loginHint={loginHint}
              emailHint={email}
              error={error}
              isLoading={isResendingCode || isVerifying}
              verificationType={verificationType}
              smsLoginSupported={smsLoginSupported}
            /> }

          {/* Google Login Option */}
          {allowGoogleLoginForExistingPatients && (
              <div className="mb-6">
                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or verify using Google login</span>
                  </div>
                </div>

                <div className="max-w-md mx-auto">
                  <div className="bg-white shadow-xl rounded-2xl overflow-hidden max-w-lg mx-auto p-6">
                    <GoogleLoginButton
                      onSuccess={handleGoogleLogin}
                      onError={(error) => {
                        captureException(error);
                        setError("Failed to initialize Google login. Please try the verification code option.");
                      }}
                      disabled={isResendingCode || isVerifying}
                    />
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
